import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { searchMailRecipients } from '@/api/onsiteLiveAdmin';
import type { MailRecipient } from '@/types/oscar';
import { FieldError } from '../BranchRelationships/shared';
import { btnSecondary, inputCls } from '../BranchRelationships/styles';

/**
 * The email divert: pick a real Exchange / Entra address (Anthony, 25/10/2026:
 * "needs to be a legitimate email in exchange/entra"). Searches Entra mailboxes,
 * shared mailboxes and mail-enabled groups by name or address - never guests or
 * leavers - and commits only a picked address; typed text is never saved. The
 * server checks a changed address again on save.
 */
export function MailRecipientPicker({
  value, onChange, canWrite, error, hint,
}: {
  /** The address; '' = none (the mailbox is locked instead). */
  value: string;
  onChange: (address: string) => void;
  canWrite: boolean;
  error?: string;
  hint?: string | null;
}) {
  const [query, setQuery] = useState(value);
  const [debounced, setDebounced] = useState(value);
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState<MailRecipient | null>(null);

  useEffect(() => { setQuery(value); setPicked((p) => (p && p.Address === value ? p : null)); }, [value]);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  const results = useQuery({
    queryKey: ['onsitelive-admin', 'mail-recipients', debounced],
    queryFn: ({ signal }) => searchMailRecipients(debounced, signal),
    enabled: canWrite && open && debounced.length >= 2 && debounced !== value,
    retry: false,
  });
  const showMenu = open && debounced.length >= 2 && debounced !== value;
  const rows = results.data ?? [];

  const commit = (r: MailRecipient) => { setPicked(r); setQuery(r.Address); setOpen(false); onChange(r.Address); };

  return (
    <div className="flex flex-col text-xs font-semibold text-darkGrey">
      <span>Email divert</span>
      {canWrite ? (
        <div className="mt-1 flex items-center gap-2 font-normal">
          <div className="relative flex-1">
            <input
              value={query}
              onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
              onFocus={() => setOpen(true)}
              onBlur={() => { setOpen(false); setQuery(value); }}
              placeholder="Search Entra by name or address"
              aria-label="Email divert - search Entra by name or address"
              autoComplete="off"
              role="combobox"
              aria-expanded={showMenu}
              className={`${inputCls} w-full font-normal`}
            />
            {showMenu && (
              <ul className="absolute z-10 mt-1 max-h-56 w-full overflow-y-auto rounded border border-border bg-white text-sm shadow-md">
                {results.isLoading && <li className="px-3 py-2 text-darkGrey/60">Searching Entra…</li>}
                {results.isError && <li className="px-3 py-2 text-red">Entra lookup unavailable.</li>}
                {!results.isLoading && !results.isError && rows.length === 0 && (
                  <li className="px-3 py-2 text-darkGrey/60">No mailbox or group matches “{debounced}”.</li>
                )}
                {rows.map((r) => (
                  <li key={r.Address}>
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => commit(r)}
                      className="block w-full px-3 py-1.5 text-left hover:bg-bg"
                    >
                      <span className="font-medium text-navy">{r.DisplayName}</span>
                      <span className="ml-1.5 text-xs text-darkGrey/60">{r.Address} · {r.Kind}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {value && (
            <button type="button" className={btnSecondary} onClick={() => { setPicked(null); onChange(''); }} aria-label="Clear email divert">
              Clear
            </button>
          )}
        </div>
      ) : (
        <span className="mt-1 text-sm font-normal text-darkGrey/80">{value || '—'}</span>
      )}
      {picked && <span className="mt-1 font-normal text-darkGrey/70">{picked.DisplayName} · {picked.Kind}</span>}
      {hint && <span className="mt-1 font-normal text-darkGrey/70">{hint}</span>}
      {error && <FieldError message={error} />}
    </div>
  );
}
